import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const P  = '#C29500';
const P2 = '#E8C040';

const KudiLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="rp-g" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={P2}/>
        <stop offset="100%" stopColor={P}/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#rp-g)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function ResetPassword() {
  const [params]            = useSearchParams();
  const token               = params.get('token') || '';
  const navigate            = useNavigate();
  const [pw, setPw]         = useState('');
  const [pw2, setPw2]       = useState('');
  const [show, setShow]     = useState(false);
  const [loading, setLoad]  = useState(false);
  const [done, setDone]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8)  return toast.error('Password must be at least 8 characters');
    if (pw !== pw2)     return toast.error('Passwords do not match');
    setLoad(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
      toast.success('Password updated!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset link is invalid or expired');
    } finally {
      setLoad(false);
    }
  };

  const shell = (child) => (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <KudiLogo size={40}/>
            <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>Kudi<span style={{ color: P2 }}>bot</span></span>
          </Link>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 32 }}>
          {child}
        </div>
      </motion.div>
      <style>{`input::placeholder { color: #334155; }`}</style>
    </div>
  );

  if (!token) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <AlertTriangle size={44} style={{ color: '#f59e0b', margin: '0 auto 14px' }}/>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>Invalid reset link</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 24px' }}>This link is missing its token. Request a new one.</p>
        <Link to="/forgot-password" style={{ color: P2, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>Request a new link →</Link>
      </div>
    );
  }

  if (done) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <CheckCircle size={44} style={{ color: P2, margin: '0 auto 14px' }}/>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>Password updated</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 24px' }}>Redirecting you to sign in…</p>
        <Link to="/login" style={{ color: P2, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>Go to sign in →</Link>
      </div>
    );
  }

  const field = (val, set, ph, label) => (
    <div>
      <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8, fontWeight: 600 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <Lock size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
        <input type={show ? 'text' : 'password'} placeholder={ph}
          value={val} onChange={e => set(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', background: '#0D0D12', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 42px 12px 42px', color: '#fff', fontSize: 14, outline: 'none' }}/>
        <button type="button" onClick={() => setShow(!show)}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#475569' }}>
          {show ? <EyeOff size={18}/> : <Eye size={18}/>}
        </button>
      </div>
    </div>
  );

  return shell(
    <>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>Set a new password</h1>
      <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 22px' }}>Choose a strong password of at least 8 characters.</p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {field(pw,  setPw,  '••••••••', 'New Password')}
        {field(pw2, setPw2, '••••••••', 'Confirm New Password')}
        <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} type="submit" disabled={loading}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#0A0A0F', background: `linear-gradient(135deg, ${P2}, ${P})`, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Updating...' : <><span>Update password</span><ArrowRight size={16}/></>}
        </motion.button>
      </form>
    </>
  );
}
